import { useCallback, useEffect, useRef, useState } from "react";
import {
  listAudioDevices,
  openMicrophone,
  routeAudioOutput,
  type AudioDeviceInventory,
} from "../lib/audioDevices";

const INPUT_STORAGE_KEY = "table-interpreter.input";
const OUTPUT_STORAGE_KEY = "table-interpreter.output";
const OUTPUT_MODE_STORAGE_KEY = "table-interpreter.output-mode";
const ABORTED_OUTPUT_ROUTE = Symbol("aborted-output-route");

function settleOutputRouteOnAbort<T>(
  promise: Promise<T>,
  signal?: AbortSignal,
  canSettleEarly: () => boolean = () => true,
): Promise<T | typeof ABORTED_OUTPUT_ROUTE> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.resolve(ABORTED_OUTPUT_ROUTE);

  return new Promise((resolve, reject) => {
    const onAbort = () => {
      if (canSettleEarly()) {
        signal.removeEventListener("abort", onAbort);
        resolve(ABORTED_OUTPUT_ROUTE);
      }
    };
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(
      value => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      caught => {
        signal.removeEventListener("abort", onAbort);
        if (signal.aborted) resolve(ABORTED_OUTPUT_ROUTE);
        else reject(caught);
      },
    );
  });
}

type InputMeter = { readLevel(): number; stop(): void };

export type AudioDeviceHookDependencies = {
  mediaDevices?: MediaDevices;
  storage?: Storage;
  outputSelectionSupported?: boolean;
  createMeter?: (stream: MediaStream) => InputMeter;
};

export type UseAudioDevicesResult = {
  permission: "prompt" | "granted" | "denied";
  inventory: AudioDeviceInventory;
  selectedInputId: string;
  selectedOutputId: string;
  outputSelectionSupported: boolean;
  microphoneStream: MediaStream | null;
  inputLevel: number;
  error: string | null;
  requestPermission(): Promise<MediaStream>;
  selectInput(deviceId: string): Promise<void>;
  selectOutput(
    deviceId: string,
    audioElement: HTMLAudioElement,
    signal?: AbortSignal,
  ): Promise<void>;
  testInput(): Promise<void>;
  testOutput(audioElement: HTMLAudioElement): Promise<void>;
  releaseMicrophone(): void;
};

export function useAudioDevices(
  dependencies: AudioDeviceHookDependencies = {},
): UseAudioDevicesResult {
  const mediaDevices = dependencies.mediaDevices ?? navigator.mediaDevices;
  const storage = dependencies.storage ?? window.localStorage;
  const [permission, setPermission] = useState<UseAudioDevicesResult["permission"]>("prompt");
  const [inventory, setInventory] = useState<AudioDeviceInventory>({ inputs: [], outputs: [] });
  const [selectedInputId, setSelectedInputId] = useState(() => storage.getItem(INPUT_STORAGE_KEY) || "default");
  const [selectedOutputId, setSelectedOutputId] = useState(() => storage.getItem(OUTPUT_STORAGE_KEY) || "default");
  const [microphoneStream, setMicrophoneStream] = useState<MediaStream | null>(null);
  const [inputLevel, setInputLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const meterRef = useRef<InputMeter | null>(null);
  const meterFrameRef = useRef<number | null>(null);
  const selectedInputRef = useRef(selectedInputId);
  const selectedOutputRef = useRef(selectedOutputId);
  const mediaRequestRef = useRef(0);
  const outputRouteGenerationRef = useRef(0);
  const outputRouteQueueRef = useRef<Promise<void>>(Promise.resolve());
  const latestOutputRouteRef = useRef<Promise<void>>(Promise.resolve());
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;
  selectedInputRef.current = selectedInputId;

  const outputSelectionSupported = dependencies.outputSelectionSupported ?? (
    typeof (HTMLMediaElement.prototype as HTMLMediaElement & { setSinkId?: unknown }).setSinkId === "function"
  );

  const stopMeter = useCallback(() => {
    if (meterFrameRef.current !== null) cancelAnimationFrame(meterFrameRef.current);
    meterFrameRef.current = null;
    meterRef.current?.stop();
    meterRef.current = null;
    setInputLevel(0);
  }, []);

  const startMeter = useCallback((stream: MediaStream) => {
    stopMeter();
    const meter = (dependenciesRef.current.createMeter ?? createInputMeter)(stream);
    meterRef.current = meter;
    const sample = () => {
      setInputLevel(meter.readLevel());
      meterFrameRef.current = requestAnimationFrame(sample);
    };
    meterFrameRef.current = requestAnimationFrame(sample);
  }, [stopMeter]);

  const installStream = useCallback((stream: MediaStream) => {
    const previous = streamRef.current;
    streamRef.current = stream;
    setMicrophoneStream(stream);
    startMeter(stream);
    if (previous !== stream) previous?.getTracks().forEach(track => track.stop());
  }, [startMeter]);

  const refreshInventory = useCallback(async () => {
    try {
      const next = await listAudioDevices(mediaDevices);
      setInventory(next);
      const inputIds = new Set(next.inputs.map(device => device.deviceId));
      const outputIds = new Set(next.outputs.map(device => device.deviceId));
      if (next.inputs.length && !inputIds.has(selectedInputRef.current)) {
        const fallback = inputIds.has("default") ? "default" : next.inputs[0]!.deviceId;
        selectedInputRef.current = fallback;
        setSelectedInputId(fallback);
        storage.setItem(INPUT_STORAGE_KEY, fallback);
      }
      setSelectedOutputId(current => {
        if (!next.outputs.length || outputIds.has(current)) return current;
        const fallback = outputIds.has("default") ? "default" : next.outputs[0]!.deviceId;
        selectedOutputRef.current = fallback;
        storage.setItem(OUTPUT_STORAGE_KEY, fallback);
        return fallback;
      });
    } catch {
      setInventory({ inputs: [], outputs: [] });
    }
  }, [mediaDevices, storage]);

  const requestPermission = useCallback(async () => {
    const request = ++mediaRequestRef.current;
    try {
      const opened = await openMicrophone(mediaDevices, selectedInputRef.current);
      const { stream } = opened;
      if (mediaRequestRef.current !== request) {
        stream.getTracks().forEach(track => track.stop());
        throw new Error("microphone-cancelled");
      }
      if (selectedInputRef.current !== opened.deviceId) {
        selectedInputRef.current = opened.deviceId;
        setSelectedInputId(opened.deviceId);
        storage.setItem(INPUT_STORAGE_KEY, opened.deviceId);
      }
      installStream(stream);
      setPermission("granted");
      setError(null);
      await refreshInventory();
      return stream;
    } catch (caught) {
      if (mediaRequestRef.current !== request) throw caught;
      const denied = caught instanceof DOMException && caught.name === "NotAllowedError";
      setPermission(denied ? "denied" : "prompt");
      setError(denied ? "microphone-permission" : "no-microphone");
      throw caught;
    }
  }, [installStream, mediaDevices, refreshInventory, storage]);

  const selectInput = useCallback(async (deviceId: string) => {
    if (!streamRef.current) {
      selectedInputRef.current = deviceId;
      setSelectedInputId(deviceId);
      storage.setItem(INPUT_STORAGE_KEY, deviceId);
      return;
    }
    const request = ++mediaRequestRef.current;
    try {
      const opened = await openMicrophone(mediaDevices, deviceId);
      const { stream } = opened;
      if (mediaRequestRef.current !== request) {
        stream.getTracks().forEach(track => track.stop());
        throw new Error("microphone-cancelled");
      }
      selectedInputRef.current = opened.deviceId;
      setSelectedInputId(opened.deviceId);
      storage.setItem(INPUT_STORAGE_KEY, opened.deviceId);
      installStream(stream);
      setError(null);
    } catch (caught) {
      if (mediaRequestRef.current !== request) throw caught;
      setError("no-microphone");
      throw caught;
    }
  }, [installStream, mediaDevices, storage]);

  const selectOutput = useCallback(async (
    deviceId: string,
    audioElement: HTMLAudioElement,
    signal?: AbortSignal,
  ) => {
    const generation = ++outputRouteGenerationRef.current;
    const routeLatestOutput = async () => {
      if (signal?.aborted || outputRouteGenerationRef.current !== generation) return;
      let sinkWriteStarted = false;
      try {
        const route = await settleOutputRouteOnAbort(
          routeAudioOutput(
            mediaDevices as MediaDevices & {
              selectAudioOutput?: (options: { deviceId: string }) => Promise<{ deviceId: string }>;
            },
            audioElement,
            outputSelectionSupported ? deviceId : "default",
            signal,
            () => {
              sinkWriteStarted = true;
            },
          ),
          signal,
          () => !sinkWriteStarted,
        );
        if (
          route === ABORTED_OUTPUT_ROUTE ||
          signal?.aborted ||
          outputRouteGenerationRef.current !== generation
        ) return;
        const persistedId = route.mode === "selected" ? route.deviceId : "default";
        selectedOutputRef.current = persistedId;
        setSelectedOutputId(persistedId);
        storage.setItem(OUTPUT_STORAGE_KEY, persistedId);
        storage.setItem(OUTPUT_MODE_STORAGE_KEY, route.mode);
        setError(null);
      } catch (caught) {
        if (signal?.aborted || outputRouteGenerationRef.current !== generation) return;
        setError("audio-output");
        throw caught;
      }
    };

    const queued = outputRouteQueueRef.current.then(routeLatestOutput, routeLatestOutput);
    latestOutputRouteRef.current = queued;
    outputRouteQueueRef.current = queued.catch(() => {});
    await queued;
    while (outputRouteGenerationRef.current !== generation) {
      if (signal?.aborted) return;
      const latestRoute = latestOutputRouteRef.current;
      await latestRoute;
      if (latestRoute === latestOutputRouteRef.current) return;
    }
  }, [mediaDevices, outputSelectionSupported, storage]);

  const testInput = useCallback(async () => {
    if (!streamRef.current) await requestPermission();
  }, [requestPermission]);

  const testOutput = useCallback(async (audioElement: HTMLAudioElement) => {
    const generation = outputRouteGenerationRef.current;
    const routeTestTone = async () => {
      if (outputRouteGenerationRef.current !== generation) return;
      try {
        await routeAudioOutput(
          mediaDevices as MediaDevices & {
            selectAudioOutput?: (options: { deviceId: string }) => Promise<{ deviceId: string }>;
          },
          audioElement,
          outputSelectionSupported ? selectedOutputRef.current : "default",
        );
        if (outputRouteGenerationRef.current !== generation) return;
        audioElement.srcObject = null;
        audioElement.src = createTestToneDataUrl();
        audioElement.currentTime = 0;
        audioElement.volume = 0.9;
        audioElement.muted = false;
        await audioElement.play();
        if (outputRouteGenerationRef.current === generation) setError(null);
      } catch (caught) {
        if (outputRouteGenerationRef.current !== generation) return;
        setError("audio-output");
        throw caught;
      }
    };

    const queued = outputRouteQueueRef.current.then(routeTestTone, routeTestTone);
    outputRouteQueueRef.current = queued.catch(() => {});
    await queued;
  }, [mediaDevices, outputSelectionSupported]);

  const releaseMicrophone = useCallback(() => {
    mediaRequestRef.current += 1;
    stopMeter();
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    setMicrophoneStream(null);
  }, [stopMeter]);

  useEffect(() => {
    void refreshInventory();
    const onDeviceChange = () => void refreshInventory();
    mediaDevices.addEventListener("devicechange", onDeviceChange);
    return () => {
      mediaRequestRef.current += 1;
      outputRouteGenerationRef.current += 1;
      mediaDevices.removeEventListener("devicechange", onDeviceChange);
      if (meterFrameRef.current !== null) cancelAnimationFrame(meterFrameRef.current);
      meterRef.current?.stop();
      streamRef.current?.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    };
  }, [mediaDevices, refreshInventory]);

  return {
    permission,
    inventory,
    selectedInputId,
    selectedOutputId,
    outputSelectionSupported,
    microphoneStream,
    inputLevel,
    error,
    requestPermission,
    selectInput,
    selectOutput,
    testInput,
    testOutput,
    releaseMicrophone,
  };
}

function createInputMeter(stream: MediaStream): InputMeter {
  const AudioContextConstructor = window.AudioContext;
  if (!AudioContextConstructor) return { readLevel: () => 0, stop() {} };
  const context = new AudioContextConstructor();
  const analyser = context.createAnalyser();
  analyser.fftSize = 256;
  const source = context.createMediaStreamSource(stream);
  source.connect(analyser);
  const samples = new Uint8Array(analyser.fftSize);
  return {
    readLevel() {
      analyser.getByteTimeDomainData(samples);
      let sum = 0;
      for (const sample of samples) {
        const normalized = (sample - 128) / 128;
        sum += normalized * normalized;
      }
      return Math.min(1, Math.sqrt(sum / samples.length) * 3);
    },
    stop() {
      source.disconnect();
      analyser.disconnect();
      void context.close();
    },
  };
}

function createTestToneDataUrl(): string {
  const sampleRate = 8_000;
  const sampleCount = Math.round(sampleRate * 0.18);
  const dataLength = sampleCount * 2;
  const buffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(buffer);
  writeAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + dataLength, true);
  writeAscii(view, 8, "WAVE");
  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeAscii(view, 36, "data");
  view.setUint32(40, dataLength, true);
  for (let index = 0; index < sampleCount; index += 1) {
    const fade = Math.min(1, index / 80, (sampleCount - index) / 80);
    const sample = Math.sin(2 * Math.PI * 660 * index / sampleRate) * 0.25 * fade;
    view.setInt16(44 + index * 2, Math.round(sample * 32767), true);
  }
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return `data:audio/wav;base64,${btoa(binary)}`;
}

function writeAscii(view: DataView, offset: number, value: string): void {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
}
